import {
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  TextField,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { RdsInstanceClassSelect } from '@/components/selectors/rdsInstanceClassSelect';
import { RDSSubnetGroupSelect } from '@/components/selectors/rdsSubnetGroupSelect';
import { RestoreRegionSelect } from '@/components/selectors/restoreRegionSelect';
import { SecurityGroupSelect } from '@/components/selectors/securityGroupSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreRDSState } from '../restoreRDSWizard';

export const RDSConfiguration = (props: StepProps<RestoreRDSState>) => {
  const dal = useDAL();

  const [error, setError] = useState<string | undefined>();
  const { RdsRestoreWithoutEncryptionEnabled } = useFeatureFlags();
  const [isRestoring, setIsRestoring] = useState(false);

  const { data: inventoryResourceBody } = dal.inventory.useResource(
    props.wizardState.resourceId
  );

  useEffect(() => {
    props.setWizardState((state) => ({
      ...state,
      securityGroupIds: undefined,
      rdsInstanceClass: undefined,
      vpc: undefined,
      rdsSubnetGroupName: undefined,
      regionName: state.initialRegionName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.restoreAccount]);

  const canGoNext = (): boolean => {
    return (
      !isRestoring &&
      !!props.wizardState.regionName &&
      !!props.wizardState.resourceName &&
      !!props.wizardState.securityGroupIds?.length &&
      !!props.wizardState.rdsSubnetGroupName &&
      (!props.wizardState.encryption?.isEncrypted ||
        !!getEncryptionKey(props.wizardState.encryption))
    );
  };

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);

    const tags = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon:restore': 'true',
    };

    const encryptionKeyId = props.wizardState.encryption?.isEncrypted
      ? getEncryptionKey(props.wizardState.encryption) || ''
      : '';

    return dal.restore
      .database(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        destination: {
          awsRds: {
            tags,
            encryptionKeyId,
            restoredName: props.wizardState.resourceName || '',
            restoreRegion: props.wizardState.regionName || '',
            securityGroups: props.wizardState.securityGroupIds,
            dbInstanceClass: props.wizardState.rdsInstanceClass,
            subnetGroup: props.wizardState.rdsSubnetGroupName,
          },
        },
      })
      .then(() =>
        props.finish(
          `Started restore job for ${props.wizardState.resourceName}`
        )
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setIsRestoring(false);
        setError(`Failed to restore RDS: ${errorMessage}`);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <FormControl size='small' className='flex-1'>
            <FormLabel>Database name</FormLabel>
            <TextField
              data-testid='databaseNameTextField'
              size='small'
              value={props.wizardState.resourceName || ''}
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  resourceName: event.target.value,
                }))
              }
            />
          </FormControl>
          <RestoreRegionSelect
            isRequired
            cloudProvider={Provider.Aws}
            restoreAccountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            onChange={(region) =>
              props.setWizardState((state) => ({
                ...state,
                regionName: region,
                rdsSubnetGroupName: undefined,
                securityGroupIds: undefined,
                vpc: undefined,
                rdsInstanceClass: undefined,
              }))
            }
          />
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <RDSSubnetGroupSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            rdsSubnetGroupName={props.wizardState.rdsSubnetGroupName}
            onChange={(rdsSubnetGroupName, vpc) =>
              props.setWizardState((state) => ({
                ...state,
                rdsSubnetGroupName,
                vpc,
                securityGroupIds: undefined,
              }))
            }
          />
          <SecurityGroupSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            securityGroupIds={props.wizardState.securityGroupIds}
            vpc={props.wizardState.vpc || ''}
            isSubnetIdMandatory={false}
            isOptionalField={false}
            onChange={(securityGroupIds) =>
              props.setWizardState((state) => ({
                ...state,
                securityGroupIds,
              }))
            }
          />
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <RdsInstanceClassSelect
            isOptionalField={true}
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            engine={
              inventoryResourceBody?.resource.resourceProperties?.awsRds?.engine
            }
            engineVersion={
              inventoryResourceBody?.resource.resourceProperties?.awsRds
                ?.engineVersion
            }
            rdsInstanceClass={props.wizardState.rdsInstanceClass}
            isCluster={
              !!inventoryResourceBody?.resource?.resourceProperties?.awsRds
                ?.clusterId
            }
            onChange={(rdsInstanceClass) =>
              props.setWizardState((state) => ({
                ...state,
                rdsInstanceClass,
              }))
            }
          />
          <div className='flex-1' />
        </Stack>
      </Box>
      <Divider />
      {props.wizardState.regionName && (
        <>
          <CardContent className='p-0'>
            <EncryptionComponent
              canBeTurnedOff={RdsRestoreWithoutEncryptionEnabled}
              encryption={props.wizardState.encryption}
              accountId={props.wizardState.restoreAccount!.id}
              regionName={props.wizardState.regionName}
              onChange={(encryption) =>
                props.setWizardState((state) => ({
                  ...state,
                  encryption,
                }))
              }
            />
          </CardContent>
          <Divider />
        </>
      )}
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
        />
      </CardContent>
    </StepContainer>
  );
};
